import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  AutocompleteOption,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  Table,
  Typography,
} from "@mui/joy";
import { SxProps } from "@mui/joy/styles/types";
import { ApiCompetition, ApiCompetitionLookupMatch } from "griptonite-api-client";
import { orderBy } from "lodash";
import { ComponentProps, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { useDebounce } from "use-debounce";
import { z } from "zod";

import { Route } from "@/core/types";
import {
  useCompetitionQuery,
  useCompetitionUserLookupQuery,
  useCurrentCompetitionUuid,
} from "@/hooks/competitions";
import { useCompetitionRoutesQuery } from "@/hooks/routes";
import { useCurrentVenueUuid } from "@/hooks/venues";
import getCompetitionRouteDisplay from "@/utils/getCompetitionRouteDisplay";

import Avatar from "../Avatar";
import FormErrorAlert from "../FormErrorAlert";
import SelectInput from "../forms/SelectInput";
import TextInput, { NumberInput } from "../forms/TextInput";
import { ToggleButtonSelectInput } from "../forms/ToggleButtonSelect";
import { RouteCircle } from "../RouteCircle";
import { Counter } from "./CompetitionEntriesTable";

export const CreateEntryFormSchema = z.intersection(
  z.object({
    category: z.string({ invalid_type_error: "Required" }),
    attempts: z
      .record(
        z.string(),
        z.object({
          count: z.number().int().min(1).max(100).optional(),
          segmentsCount: z.number().int().min(0).max(100).nullable().optional(),
          success: z.boolean().optional(),
          segmentTriesCount: z.number().int().min(0).max(100).nullable().optional(),
        }),
      )
      .optional(),
  }),
  z.discriminatedUnion("mode", [
    z.object({
      mode: z.literal("lookup"),
      user: z.string(),
    }),
    z.object({
      mode: z.literal("create"),
      profile: z.object({
        firstName: z.string(),
        lastName: z.string(),
        gender: z.enum(["MALE", "FEMALE", "OTHER"]).nullable(),
        email: z.string().email().nullable().optional(),
        yearOfBirth: z.number().int().min(1920).max(new Date().getFullYear()).nullable().optional(),
      }),
    }),
  ]),
);

export type CreateEntryFormValues = z.infer<typeof CreateEntryFormSchema>;

const SubmitButton = () => {
  const { formState } = useFormContext();
  return (
    <Box sx={{ padding: 2, display: "flex" }}>
      <Button
        type="submit"
        loading={formState.isSubmitting}
        loadingPosition="start"
        sx={{ flex: 1 }}
        disabled={formState.isSubmitting}
      >
        Submit
      </Button>
    </Box>
  );
};

const ZoneCheckbox = ({ name, zone }: { name: string; zone: number }) => {
  const { control } = useFormContext();
  const { field } = useController({
    name,
    control,
  });

  return (
    <Checkbox
      size="lg"
      checked={!!field.value && field.value >= zone}
      onChange={(e) => {
        field.onChange(e.target.checked ? zone : 0);
      }}
    />
  );
};

const SuccessCheckbox = ({ name }: { name: string }) => {
  const { control } = useFormContext();
  const { field } = useController({
    name,
    control,
  });

  return (
    <Checkbox
      size="lg"
      checked={!!field.value}
      onChange={(e) => {
        field.onChange(e.target.checked);
      }}
    />
  );
};

const AttemptsFields = () => {
  const competitionUuid = useCurrentCompetitionUuid();
  const venueUuid = useCurrentVenueUuid();
  const competitionQuery = useCompetitionQuery(competitionUuid, venueUuid);
  const competition = competitionQuery.data;

  const routesQuery = useCompetitionRoutesQuery(competitionUuid, venueUuid);
  const routes = routesQuery.data;

  const categoryUuid = useWatch({ name: "category" });
  const routesForCategory = useMemo(() => {
    return orderBy(
      routes?.filter((route) =>
        route.competitionRouteGroups.some((crg) => crg.categories.includes(categoryUuid)),
      ),
      (route) =>
        getCompetitionRouteDisplay(route, {
          routeGroups: competition?.routeGroups || [],
          categoryUuid,
        }).number,
    );
  }, [routes, categoryUuid, competition?.routeGroups]);

  if (!competition || !routesForCategory) {
    if (competitionQuery.isLoading || routesQuery.isLoading) {
      return <Typography>Loading...</Typography>;
    }
    return null;
  }

  const segmentsCount = routesForCategory?.[0]?.segmentsCount;

  return (
    <Table
      sx={{
        display: "grid",
        gridTemplateColumns:
          competition.scoringSystem === "IFSC_RANKING"
            ? "1fr repeat(4, auto)"
            : competition.climbType === "BOULDER" && segmentsCount
              ? `1fr repeat(${2 + segmentsCount}, auto)`
              : "1fr repeat(2, auto)",
        "tbody, thead, tr": {
          display: "contents",
        },
        "th, td": {
          display: "flex",
          alignItems: "center",
          padding: 0.5,
        },
        "thead tr th:first-child, tbody tr td:first-child": {
          paddingLeft: 2,
        },
        "thead tr th:last-child, tbody tr td:last-child": {
          paddingRight: 2,
        },
        "th.success, td.success, th.zone, td.zone": {
          paddingX: 1,
        },
        "th.success, td.success, th.zone, td.zone, th.attempts, td.attempts, th.zone-attempts, td.zone-attempts, th.quickdraws, td.quickdraws":
          {
            justifyContent: "flex-end",
          },
      }}
    >
      <thead>
        <tr>
          <th className="route">Route</th>
          <th className="success">Top</th>
          {competition.climbType === "BOULDER" && segmentsCount === 1 ? (
            <th className="zone">Zone</th>
          ) : null}
          {competition.climbType === "BOULDER" && segmentsCount === 2 ? (
            <>
              <th className="zone">Z1</th>
              <th className="zone">Z2</th>
            </>
          ) : null}
          {competition.climbType === "BOULDER" ? (
            <th className="attempts">
              Tries
              {competition.scoringSystem === "IFSC_RANKING" ? " to Top" : null}
            </th>
          ) : null}
          {competition.climbType === "SPORT" ? <th className="quickdraws">QDs</th> : null}
          {competition.scoringSystem === "IFSC_RANKING" ? (
            <th className="zone-attempts">Tries to Zone</th>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {routesForCategory?.map((route) => (
          <AttemptRow
            route={route}
            competition={competition}
            categoryUuid={categoryUuid}
            key={route.uuid}
          />
        ))}
      </tbody>
    </Table>
  );
};

const CounterInput = ({
  name,
  sx,
  ...props
}: Omit<ComponentProps<typeof Counter>, "onChange" | "value"> & {
  name: string;
  sx?: SxProps;
}) => {
  const { control } = useFormContext();
  const { field } = useController({
    name,
    control,
  });
  return (
    <Box sx={sx}>
      <Counter
        {...props}
        onChange={(value) => {
          field.onChange(value);
        }}
        value={field.value}
      />
    </Box>
  );
};

const AttemptRow = ({
  route,
  competition,
  categoryUuid,
}: {
  route: Route;
  competition: ApiCompetition;
  categoryUuid: string;
}) => {
  const { setValue, watch } = useFormContext();

  const success = watch(`attempts.${route.uuid}.success`);
  const tries = watch(`attempts.${route.uuid}.count`);
  const segmentsCount = watch(`attempts.${route.uuid}.segmentsCount`);
  const segmentTries = watch(`attempts.${route.uuid}.segmentTriesCount`);

  const triesRef = useRef(tries);
  useLayoutEffect(() => {
    triesRef.current = tries;
  }, [tries]);

  const segmentTriesRef = useRef(segmentTries);
  useLayoutEffect(() => {
    segmentTriesRef.current = segmentTries;
  }, [segmentTries]);

  useEffect(() => {
    if (success) {
      if (competition.climbType === "BOULDER") {
        setValue(`attempts.${route.uuid}.segmentsCount`, route.segmentsCount);
      } else {
        setValue(`attempts.${route.uuid}.segmentsCount`, null);
      }
      setValue(`attempts.${route.uuid}.count`, Math.max(triesRef.current || 0, 1));
      if (competition.scoringSystem === "IFSC_RANKING") {
        setValue(
          `attempts.${route.uuid}.segmentTriesCount`,
          Math.max(segmentTriesRef.current || 0, 1),
        );
      }
    } else if (success === false) {
      if (competition.climbType === "BOULDER") {
        setValue(`attempts.${route.uuid}.segmentsCount`, 0);
      }
    }
  }, [
    success,
    competition.scoringSystem,
    competition.climbType,
    route.uuid,
    setValue,
    route.segmentsCount,
  ]);

  useEffect(() => {
    if (segmentsCount) {
      setValue(`attempts.${route.uuid}.count`, Math.max(triesRef.current || 0, 1));
      if (competition.scoringSystem === "IFSC_RANKING") {
        setValue(
          `attempts.${route.uuid}.segmentTriesCount`,
          Math.max(segmentTriesRef.current || 0, 1),
        );
      }
      if (competition.climbType === "SPORT") {
        setValue(`attempts.${route.uuid}.success`, false);
      }
    } else if (competition.climbType === "BOULDER" && segmentsCount === 0) {
      setValue(`attempts.${route.uuid}.success`, false);
    }
  }, [segmentsCount, competition.scoringSystem, competition.climbType, route.uuid, setValue]);

  return (
    <tr key={route.uuid}>
      <td className="route">
        <Stack direction="row" alignItems="center" gap={0.5}>
          <RouteCircle
            colors={(route.colors || "#FFFFFF").split(":")}
            climbType={route.climbType}
          />
          <Typography fontWeight="bold">
            {
              getCompetitionRouteDisplay(route, {
                routeGroups: competition.routeGroups || [],
                categoryUuid,
              }).number
            }
          </Typography>
        </Stack>
      </td>
      <td className="success">
        <SuccessCheckbox name={`attempts.${route.uuid}.success`} />
      </td>
      {competition.climbType === "BOULDER" ? (
        <>
          {(route.segmentsCount || 0) > 0 ? (
            <td className="zone">
              <ZoneCheckbox name={`attempts.${route.uuid}.segmentsCount`} zone={1} />
            </td>
          ) : null}
          {route.segmentsCount === 2 ? (
            <td className="zone">
              <ZoneCheckbox name={`attempts.${route.uuid}.segmentsCount`} zone={2} />
            </td>
          ) : null}
          <td className="attempts">
            <CounterInput
              name={`attempts.${route.uuid}.count`}
              sx={{
                width: "100px",
              }}
              min={success ? 1 : 0}
              max={100}
            />
          </td>
          {competition.scoringSystem === "IFSC_RANKING" ? (
            <td className="zone-attempts">
              <CounterInput
                name={`attempts.${route.uuid}.segmentTriesCount`}
                sx={{
                  width: "100px",
                }}
                min={0}
                max={100}
              />
            </td>
          ) : null}
        </>
      ) : (
        <>
          <td className="quickdraws">
            <CounterInput
              name={`attempts.${route.uuid}.segmentsCount`}
              sx={{
                width: "100px",
              }}
              min={0}
              max={(route.segmentsCount || 1) - 1}
            />
          </td>
        </>
      )}
    </tr>
  );
};

const currentYear = new Date().getFullYear();

export const UserLookupInput = ({
  competitionUuid,
  name,
}: {
  competitionUuid: string | undefined;
  name: string;
}) => {
  const { control, setValue } = useFormContext();
  const [selectedUser, setSelectedUser] = useState<ApiCompetitionLookupMatch | undefined>(
    undefined,
  );
  const { field, fieldState } = useController({
    name,
    control,
  });
  const [inputValue, setInputValue] = useState("");
  const [debouncedValue] = useDebounce(inputValue, 250);
  const userLookupQuery = useCompetitionUserLookupQuery(competitionUuid, debouncedValue);
  console.log({ inputValue });
  return (
    <FormControl error={Boolean(fieldState.error)}>
      <FormLabel>User</FormLabel>
      <Autocomplete
        loading={userLookupQuery.isFetching}
        autoComplete
        endDecorator={userLookupQuery.isFetching ? <CircularProgress size="sm" /> : null}
        placeholder="Type to search..."
        options={userLookupQuery.data || []}
        value={selectedUser}
        onChange={(_, option) => {
          setSelectedUser(option ?? undefined);
          field.onChange(option?.user.uuid);
          if (option?.leagueCategory) {
            setValue("category", option.leagueCategory);
          }
        }}
        inputValue={inputValue}
        onInputChange={(_, value) => setInputValue(value)}
        getOptionLabel={(option) => `${option.user.firstName} ${option.user.lastName}`}
        renderOption={(props, option) => {
          const genderLabel = {
            MALE: "M",
            FEMALE: "F",
            OTHER: "X",
            "": "",
          }[option.user.gender || ""];
          const age = option.user.yearOfBirth ? currentYear - option.user.yearOfBirth : null;
          return (
            <AutocompleteOption {...props}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar user={option.user} size={32} />
                <Stack>
                  <Typography fontWeight="bold">
                    {option.user.firstName} {option.user.lastName}
                  </Typography>
                  <Typography level="body-sm">
                    {option.user.gender ? genderLabel : null}
                    {option.user.gender && age ? ", " : null}
                    {age}
                    {option.leagueCategory ? <Chip>Entered a previous round</Chip> : null}
                  </Typography>
                </Stack>
              </Stack>
            </AutocompleteOption>
          );
        }}
      />
      {fieldState.error?.message ? (
        <FormHelperText>{fieldState.error?.message}</FormHelperText>
      ) : null}
    </FormControl>
  );
};

export const CreateEntryFormFields = () => {
  const competitionUuid = useCurrentCompetitionUuid();
  const { data: competition } = useCompetitionQuery(competitionUuid);

  const categories = competition?.stages[0].categories || [];
  const mode = useWatch({ name: "mode" });

  return (
    <Stack sx={{ flex: 1 }}>
      <Stack sx={{ flex: 1 }}>
        <FormErrorAlert />
        <Stack sx={{ padding: 2 }} spacing={2}>
          <ToggleButtonSelectInput
            label={null}
            name="mode"
            options={[
              {
                name: "Lookup existing user",
                value: "lookup",
              },
              {
                name: "Create new user",
                value: "create",
              },
            ]}
          />
          {mode === "lookup" ? (
            <UserLookupInput name="user" competitionUuid={competitionUuid} />
          ) : (
            <>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <TextInput label="First name" name="profile.firstName" />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextInput label="Last name" name="profile.lastName" />
                </Box>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <NumberInput label="Year of birth" name="profile.yearOfBirth" />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <SelectInput
                    label="Gender"
                    options={[
                      {
                        name: "Male",
                        value: "MALE",
                      },
                      {
                        name: "Female",
                        value: "FEMALE",
                      },
                      {
                        name: "Other",
                        value: "OTHER",
                      },
                    ]}
                    name="profile.gender"
                  />
                </Box>
              </Stack>
            </>
          )}
          <SelectInput
            name="category"
            label="Category"
            options={categories.map((category) => ({
              name: category.name,
              value: category.uuid,
            }))}
          />
        </Stack>
        <Accordion
          sx={{
            borderTop: "1px solid",
            borderBottom: "1px solid",
            borderColor: "neutral.300",
            ".MuiAccordionSummary-root button": { paddingX: 2, paddingY: 1.5 },
          }}
        >
          <AccordionSummary>
            <Typography level="title-md">Scorecard</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <AttemptsFields />
          </AccordionDetails>
        </Accordion>
      </Stack>
      <SubmitButton />
    </Stack>
  );
};
